import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Segment } from '@feathr/blackbox';
import { FieldCollection } from '@feathr/blackbox';
import type { IToastAction } from '@feathr/components';
import type { useExportModal } from '@feathr/extender/hooks/useExportModal';
import { useStore } from '@feathr/extender/state';

import type { IGroupExportTabHook, ISharedFormSlice } from '../GroupExportModal.types';
import type { IPersonFieldOption } from '../GroupExportModal.utils';
import {
  collectValidationErrors,
  DEFAULT_PERSON_FIELD_IDS,
  excludeNonExportablePersonFields,
  getPersonFieldOptions,
} from '../GroupExportModal.utils';
import { useDuplicateExportErrorHandler } from '../useDuplicateExportErrorHandler';

export interface IPersonFieldsSection {
  isCustomFieldsPending: boolean;
  personFieldOptions: IPersonFieldOption[];
  selectedFieldOptions: IPersonFieldOption[];
  fieldsError: string[] | undefined;
  handleSelectFields: (values: IPersonFieldOption[]) => void;
}

export interface IPersonTabState extends ISharedFormSlice, IPersonFieldsSection {}

export interface IUsePersonTabOptions {
  segment: Segment;
  form: ISharedFormSlice;
  handleExport: ReturnType<typeof useExportModal>['handleExport'];
  successToastAction?: IToastAction;
}

export type IUsePersonTabReturn = IGroupExportTabHook<IPersonTabState>;

export function usePersonTab({
  segment,
  form,
  handleExport,
  successToastAction,
}: IUsePersonTabOptions): IUsePersonTabReturn {
  const { t } = useTranslation();
  const { CustomFields } = useStore();

  const [selectedFieldIds, setSelectedFieldIds] = useState<string[]>([...DEFAULT_PERSON_FIELD_IDS]);

  const customFieldsResult = CustomFields.list({
    filters: { collection: FieldCollection.Person },
  });

  const personFieldOptions = useMemo(
    () =>
      customFieldsResult.isPending
        ? []
        : excludeNonExportablePersonFields(getPersonFieldOptions(customFieldsResult.models)),
    [customFieldsResult.isPending, customFieldsResult.models],
  );

  const selectedFieldOptions = useMemo(
    () => personFieldOptions.filter((option) => selectedFieldIds.includes(option.id)),
    [personFieldOptions, selectedFieldIds],
  );

  const fieldsError = useMemo<string[] | undefined>(
    () => (selectedFieldIds.length === 0 ? [t('Choose at least one field to export.')] : undefined),
    [selectedFieldIds.length, t],
  );

  const handleSelectFields = useCallback((values: IPersonFieldOption[]): void => {
    setSelectedFieldIds(values.map((v) => v.id));
  }, []);

  const validationErrors = useMemo(
    () => collectValidationErrors(form.emailError, form.exportNameError, fieldsError),
    [form.emailError, form.exportNameError, fieldsError],
  );

  /*
   * Fields is the only control on this tab that changes the outcome: the backend
   * fingerprint covers `export_fields` and `export_field_headers`, so a different
   * selection is a different export. Export name is in neither the fingerprint nor
   * the dedupe filter, so the copy must not imply that renaming clears the conflict.
   */
  const handleExportError = useDuplicateExportErrorHandler({
    description: t(
      'Wait for it to finish, or change the selected fields to start a different export.',
    ),
    action: successToastAction,
  });

  const handleSubmit = useCallback(async (): Promise<boolean> => {
    const fields = selectedFieldOptions.map((opt) => opt.id);
    const headers = selectedFieldOptions.map((opt) => opt.name);
    let didSucceed = false;
    await handleExport(
      () => segment.export(fields, headers, form.email, 'persons', form.exportName),
      () => {
        didSucceed = true;
      },
      t,
      { successToastAction, onError: handleExportError },
    );
    return didSucceed;
  }, [
    form.email,
    form.exportName,
    handleExport,
    handleExportError,
    segment,
    selectedFieldOptions,
    successToastAction,
    t,
  ]);

  const resetState = useCallback((): void => {
    setSelectedFieldIds([...DEFAULT_PERSON_FIELD_IDS]);
  }, []);

  return {
    state: {
      ...form,
      isCustomFieldsPending: customFieldsResult.isPending,
      personFieldOptions,
      selectedFieldOptions,
      fieldsError,
      handleSelectFields,
    },
    validationErrors,
    handleSubmit,
    resetState,
  };
}
